<template>
  <div>
    <!-- Update Availability Modal -->
    <v2-modal
      :show="show && !showOrdersStep"
      title="Update Availability"
      size="lg"
      @close="closeModal"
    >
      <div class="v2-update-availability">
        <p class="v2-text-sm v2-mb-4">
          Update availability dates for your products
          <v2-button
            size="sm"
            class="v2-ml-2"
            @click="downloadCatalog"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line
                x1="12"
                y1="15"
                x2="12"
                y2="3"
              />
            </svg>
            Download Catalog
          </v2-button>
        </p>

        <!-- Drag & Drop area -->
        <label
          v-if="!catalogFile"
          for="v2UploadCatalog"
          class="v2-update-availability__dropzone"
        >
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="v2-update-availability__upload-icon"
          >
            <polyline points="16 16 12 12 8 16" />
            <line
              x1="12"
              y1="12"
              x2="12"
              y2="21"
            />
            <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
            <polyline points="16 16 12 12 8 16" />
          </svg>
          <p class="v2-font-semibold v2-mt-2">
            Drag & Drop
          </p>
          <p class="v2-text-sm v2-text-muted">
            or click to select files from your system
          </p>
        </label>

        <!-- File selected display -->
        <div
          v-else
          class="v2-update-availability__file"
        >
          <div class="v2-flex v2-items-center v2-gap-3">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="v2-update-availability__file-icon"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <div>
              <p class="v2-text-sm v2-font-medium mb-0">
                {{ catalogFile.name }}
              </p>
              <p class="v2-text-xs v2-text-muted mb-0">
                {{ getSizeInKb(catalogFile.size) }} KB
              </p>
            </div>
          </div>
          <button
            class="v2-update-availability__remove-btn"
            @click="catalogFile = null"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        </div>

        <input
          id="v2UploadCatalog"
          type="file"
          accept=".xlsx"
          hidden
          @change="onFileSelect"
        >
      </div>

      <template #footer>
        <v2-button
          variant="outline"
          @click="closeModal"
        >
          Cancel
        </v2-button>
        <v2-button
          :disabled="isSaving || (!catalogFileLink && !catalogFile)"
          :loading="isSaving && !confirmedOrders.length"
          @click="openOrdersModal"
        >
          {{ confirmedOrders.length ? 'Next' : isSaving ? 'Saving...' : 'Save' }}
        </v2-button>
      </template>
    </v2-modal>

    <!-- Order Selection Step -->
    <v2-modal
      :show="show && showOrdersStep"
      title="Updating Orders"
      size="lg"
      @close="closeModal"
    >
      <div class="v2-update-availability__orders">
        <div class="v2-flex v2-items-center v2-gap-3 v2-mb-4">
          <div class="v2-update-availability__search-wrapper">
            <v2-input
              :value="searchText"
              placeholder="Search order id, retailer name..."
              @input="onSearchOrders"
            />
          </div>
          <v2-button
            size="md"
            :variant="isSelectedOrders ? 'outline' : 'primary'"
            @click="toggleSelectAllOrders"
          >
            {{ isSelectedOrders ? 'Deselect all' : 'Select all' }}
          </v2-button>
        </div>

        <!-- Header row -->
        <div class="v2-update-availability__orders-header">
          <div class="v2-update-availability__orders-col">
            Order Id
          </div>
          <div class="v2-update-availability__orders-col">
            Retailer Name
          </div>
        </div>

        <!-- Orders list -->
        <div class="v2-update-availability__orders-list">
          <label
            v-for="order in orderList"
            :key="order._id"
            class="v2-update-availability__order-row"
          >
            <v2-checkbox
              :value="selectedOrders.includes(order._id)"
              @input="toggleOrder(order._id)"
            />
            <div class="v2-update-availability__orders-col">
              {{ order.orderSeqId }}
            </div>
            <div class="v2-update-availability__orders-col">
              {{ order.retailerName }}
            </div>
          </label>
        </div>
      </div>

      <template #footer>
        <v2-button
          variant="outline"
          @click="closeOrderModal"
        >
          Back
        </v2-button>
        <v2-button
          :disabled="isSaving"
          :loading="isSaving"
          @click="uploadCollection"
        >
          {{ isSaving ? 'Saving...' : 'Save' }}
        </v2-button>
      </template>
    </v2-modal>
  </div>
</template>

<script>
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Checkbox from '@/components/v2/V2Checkbox.vue'
import store from '@/store'
import { UPDATE_COLLECTION } from '@/store/modules/collection.module'
import { UPLOAD_DOCUMENTS, DOWNLOAD_FILE } from '@/store/modules/common.module'
import { SHOW_DOWNLOADS_NAV_INDICATOR } from '@/store/modules/notification.module'
import { GET_ORDERS_BY_COLLECTION_SEQ_IDS } from '@/store/modules/order.module'
import { getSizeInKb, formatObject, parseErrorObject } from '@/@core/utils/utils'
import { apiToastSuccess, apiToastWarning } from '@/@core/utils/toast'
import { kpEndpoint, kpParams } from '@/axios'
import analytics, { getCollectionMixpanelObj } from '@/@core/utils/analytics'
import constants, { FILE_FORMATS } from '@/constants'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'

const { FILE_TYPES } = c

export default {
  name: 'V2UpdateAvailabilityModal',
  components: {
    V2Modal,
    V2Button,
    V2Input,
    V2Checkbox,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    collection: {
      type: Object,
      default: () => ({}),
    },
    fetchCollections: {
      type: Function,
      default() {},
    },
  },
  data() {
    return {
      catalogFile: null,
      catalogFileLink: null,
      isSaving: false,
      orderList: [],
      confirmedOrders: [],
      selectedOrders: [],
      showOrdersStep: false,
      searchText: '',
      getSizeInKb,
    }
  },
  computed: {
    isSelectedOrders() {
      return !!this.selectedOrders.length
    },
    collectionId() {
      return this.collection._id
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.getConfirmedOrders()
      }
    },
  },
  methods: {
    onFileSelect(e) {
      const file = e.target.files?.[0]
      if (file) {
        this.catalogFile = file
      }
      e.target.value = ''
    },
    downloadCatalog() {
      const payload = {
        url: kpParams(kpEndpoint.catalog.downloadProductAvailabilityFile, this.collectionId),
        fileName: `${this.collection.name}.xlsx`,
        fileFormat: FILE_FORMATS.XLSX,
      }
      store.dispatch(DOWNLOAD_FILE, payload)
        .then(() => {
          store.commit(SHOW_DOWNLOADS_NAV_INDICATOR)
        })
    },
    openOrdersModal() {
      if (this.confirmedOrders.length) {
        this.showOrdersStep = true
      } else {
        this.uploadCollection()
      }
    },
    async uploadCollection() {
      this.isSaving = true
      if (this.catalogFile && this.collectionId) {
        const payload = {
          productAvailabilityFile: await store.dispatch(UPLOAD_DOCUMENTS, { file: this.catalogFile, fileType: FILE_TYPES.COLLECTION_PRODUCTS_DETAILED }),
          orderIds: this.selectedOrders,
        }
        store
          .dispatch(UPDATE_COLLECTION, {
            collectionId: this.collectionId,
            payload,
          })
          .then(res => {
            apiToastSuccess(res.data.message)
            this.isSaving = false
            this.closeModal()
            analytics.track(
              constants.TRACKS.ACTIONS.BRAND_ENDS_UPDATING_AVAILABILITY_DATES,
              formatObject(
                getCollectionMixpanelObj(this.collection, {
                  updateAvailabilityStatus: 'Success',
                }),
              ),
            )
          })
          .catch(err => {
            this.isSaving = false
            analytics.track(
              constants.TRACKS.ACTIONS.BRAND_ENDS_UPDATING_AVAILABILITY_DATES,
              formatObject(
                getCollectionMixpanelObj(this.collection, {
                  updateAvailabilityStatus: 'Error',
                  error: parseErrorObject(err),
                }),
              ),
            )
          })
      } else {
        this.isSaving = false
        apiToastWarning('Please Select File')
      }
    },
    getConfirmedOrders() {
      store.dispatch(GET_ORDERS_BY_COLLECTION_SEQ_IDS, this.collectionId).then(res => {
        this.confirmedOrders = res.data.data.orders
        this.orderList = this.confirmedOrders
      })
    },
    onSearchOrders(searchString = '') {
      this.searchText = searchString
      const searchRegex = new RegExp(searchString, 'i')
      this.orderList = this.confirmedOrders.filter(order => searchRegex.test(order?.orderSeqId) || searchRegex.test(order?.retailerName))
    },
    toggleSelectAllOrders() {
      if (this.isSelectedOrders) {
        this.selectedOrders = []
        return
      }
      this.selectedOrders = this.orderList.map(order => order._id)
    },
    toggleOrder(orderId) {
      const idx = this.selectedOrders.indexOf(orderId)
      if (idx > -1) {
        this.selectedOrders.splice(idx, 1)
      } else {
        this.selectedOrders.push(orderId)
      }
    },
    closeOrderModal() {
      this.showOrdersStep = false
    },
    closeModal() {
      this.catalogFile = null
      this.catalogFileLink = null
      this.selectedOrders = []
      this.showOrdersStep = false
      this.searchText = ''
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-update-availability {
  &__dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-8);
    border: 2px dashed var(--v2-border);
    border-radius: var(--v2-radius-lg);
    cursor: pointer;
    transition: border-color 0.15s ease, background 0.15s ease;
    text-align: center;

    &:hover {
      border-color: var(--v2-primary);
      background: var(--v2-muted);
    }
  }

  &__upload-icon {
    color: var(--v2-primary);
  }

  &__file {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--v2-space-3) var(--v2-space-4);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-muted);
  }

  &__file-icon {
    color: var(--v2-success);
  }

  &__remove-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-1);
    background: transparent;
    border: none;
    border-radius: var(--v2-radius-sm);
    color: var(--v2-destructive);
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: rgba(229, 72, 77, 0.1);
    }
  }

  &__search-wrapper {
    flex: 1;
  }

  &__orders-header {
    display: flex;
    padding: var(--v2-space-2) var(--v2-space-3);
    background: var(--v2-muted);
    border-radius: var(--v2-radius-md);
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--v2-muted-foreground);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: var(--v2-space-1);
  }

  &__orders-col {
    flex: 1;
  }

  &__orders-list {
    max-height: 300px;
    overflow-y: auto;
  }

  &__order-row {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    padding: var(--v2-space-2) var(--v2-space-3);
    border-bottom: 1px solid var(--v2-border);
    cursor: pointer;
    transition: background 0.1s ease;
    font-size: 0.875rem;

    &:last-child {
      border-bottom: none;
    }
  }
}
</style>
